import { useCallback, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/ui';
import { cn } from '@shared/lib/cn';
import { ApiError } from '@shared/api';
import { mediaApi, type MediaLink, type MediaScope } from '@entities/media';
import { useMediaUploads } from '../model/use-media-uploads';
import { PersistedMediaItem } from './persisted-media-item';

interface MediaPickerProps {
  scope: MediaScope;
  multiple?: boolean;
  accept?: string[];
  /** The UUIDs the form holds. */
  value: string[];
  onChange: (mediaIds: string[]) => void;
  /**
   * Original {@link MediaLink} objects from the backend payload. Used to
   * render previews / open links without an extra round-trip. Newly uploaded
   * files (whose id isn't in `links`) fall back to the local file name from
   * the in-flight upload state.
   */
  links?: MediaLink[];
  label?: string;
  hint?: string;
  disabled?: boolean;
  /**
   * Hard cap on how many files may be attached (only meaningful with
   * `multiple`). Extra files in a drop/selection are trimmed and the user is
   * told; the browse button locks once the cap is reached.
   */
  maxItems?: number;
}

export function MediaPicker({
  scope,
  multiple = false,
  accept,
  value,
  onChange,
  links,
  label,
  hint,
  disabled,
  maxItems,
}: MediaPickerProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [limitNotice, setLimitNotice] = useState<string | null>(null);

  const linksById = useMemo(() => {
    const map = new Map<string, MediaLink>();
    for (const link of links ?? []) map.set(link.id, link);
    return map;
  }, [links]);

  // `addFiles` starts every upload at once, and each completion handler closes
  // over the `value` that existed when the batch started. Appending to that
  // captured array meant three concurrent uploads each wrote `[<their own id>]`
  // and the last one to resolve won — two of three photos silently vanished.
  // `onChange` comes from RHF and takes no updater function, so we track the
  // latest list in a ref and advance it eagerly: siblings resolving in the same
  // tick must see each other's ids before React has re-rendered.
  const valueRef = useRef(value);
  valueRef.current = value;

  const { items, addFiles, removeItem } = useMediaUploads({
    scope,
    accept,
    onUploaded: (result) => {
      const next = multiple
        ? valueRef.current.includes(result.mediaId)
          ? valueRef.current
          : [...valueRef.current, result.mediaId]
        : [result.mediaId];
      if (next === valueRef.current) return;
      valueRef.current = next;
      onChange(next);
    },
  });

  // Uploads are async, so a big drop would blow past the cap if we only looked
  // at `value`. Count in-flight uploads that haven't landed in `value` yet.
  const pendingCount = items.filter(
    (it) => it.status === 'uploading' || (it.result && !value.includes(it.result.mediaId)),
  ).length;
  const usedCount = value.length + pendingCount;
  const capped = multiple && maxItems != null;
  const remaining = capped ? Math.max(0, maxItems - usedCount) : Infinity;
  const atLimit = remaining === 0;

  /** Trim a selection to the remaining slots, warning the user if we cut any. */
  const acceptWithinLimit = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      if (!capped) {
        setLimitNotice(null);
        addFiles(list);
        return;
      }
      if (remaining <= 0) {
        setLimitNotice(t('media.limitReached', { max: maxItems }));
        return;
      }
      if (list.length > remaining) {
        setLimitNotice(t('media.limitTrimmed', { max: maxItems, added: remaining }));
        addFiles(list.slice(0, remaining));
        return;
      }
      setLimitNotice(null);
      addFiles(list);
    },
    [addFiles, capped, remaining, maxItems, t],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      if (e.dataTransfer.files.length > 0) {
        acceptWithinLimit(e.dataTransfer.files);
      }
    },
    [acceptWithinLimit, disabled],
  );

  const handleRemove = (mediaId: string) => {
    setLimitNotice(null);
    // Go through the same ref, so a removal can't be undone by an upload that
    // resolves before the next render.
    const next = valueRef.current.filter((id) => id !== mediaId);
    valueRef.current = next;
    onChange(next);
    mediaApi.remove(mediaId).catch((err) => {
      if (err instanceof ApiError && err.status === 404) return;
      console.warn('[media] remove failed', err);
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {label || capped ? (
        <div className="flex items-baseline justify-between gap-2">
          {label ? <div className="text-sm font-medium">{label}</div> : <span />}
          {capped ? (
            <span className="text-xs opacity-60">
              {t('media.counter', { used: usedCount, max: maxItems })}
            </span>
          ) : null}
        </div>
      ) : null}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !atLimit) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input bg-muted/20 p-6 text-center transition-colors',
          dragOver && !disabled && !atLimit && 'border-primary bg-primary/5',
          (disabled || atLimit) && 'cursor-not-allowed opacity-60',
        )}
      >
        <p className="text-sm opacity-70">
          {atLimit
            ? t('media.limitReached', { max: maxItems })
            : t('media.dropHere', { kind: multiple ? t('media.files') : t('media.file') })}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || atLimit}
          onClick={() => inputRef.current?.click()}
        >
          {t('media.browse')}
        </Button>
        {hint ? <p className="text-xs opacity-70">{hint}</p> : null}
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept?.join(',')}
          hidden
          onChange={(e) => {
            if (e.target.files) acceptWithinLimit(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {limitNotice && !atLimit ? (
        <p className="text-xs" style={{ color: 'var(--ant-color-warning)' }} role="status">
          {limitNotice}
        </p>
      ) : null}

      {value.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {value.map((mediaId) => {
            const local = items.find((it) => it.result?.mediaId === mediaId);
            return (
              <PersistedMediaItem
                key={mediaId}
                mediaId={mediaId}
                link={linksById.get(mediaId)}
                fileName={local?.file.name}
                disabled={disabled}
                onRemove={() => handleRemove(mediaId)}
              />
            );
          })}
        </ul>
      ) : null}

      {items
        .filter((it) => it.status !== 'done' || !it.result || !value.includes(it.result.mediaId))
        .map((it) => (
          <div key={it.id} className="rounded-md border bg-background px-3 py-2 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate">{it.file.name}</span>
              <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(it.id)}>
                {it.status === 'uploading' ? t('media.cancel') : t('media.dismiss')}
              </Button>
            </div>
            {it.status === 'uploading' ? (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${it.progress}%` }}
                />
              </div>
            ) : null}
            {it.status === 'error' ? (
              <p className="mt-1 text-xs" style={{ color: 'var(--ant-color-error)' }}>
                {it.error}
              </p>
            ) : null}
          </div>
        ))}
    </div>
  );
}
